"use client";
import {
	type ComponentPropsWithoutRef,
	type HTMLAttributes,
	forwardRef,
} from 'react';

import { cn } from '../utils/cn';

import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';

const cardVariants = cva(
	'rounded-xl flex flex-col text-on-surface overflow-hidden',
	{
		variants: {
			variant: {
				elevated: 'bg-surface-container-low shadow-1 hover:shadow-2',
				filled:
					'bg-surface-container-highest hover:shadow-1 focus:shadow-1 active:shadow-1',
				outlined: 'bg-surface border border-outline-variant hover:shadow-1',
			},
			clickable: {
				true: 'cursor-pointer state-layer hover:after:bg-on-surface/8 focus:after:bg-on-surface/12 active:after:bg-on-surface/12 after:transition-colors',
				false: 'cursor-default',
			},
		},
		defaultVariants: {
			variant: 'elevated',
			clickable: false,
		},
	},
);

export interface CardProps
	extends ComponentPropsWithoutRef<'div'>,
		VariantProps<typeof cardVariants> {
	asChild?: boolean;
}

export const BasicCard = ({
	className,
	variant,
	asChild = false,
	clickable,
	...props
}: CardProps) => {
	const Comp = asChild ? Slot : 'div';
	return (
		<Comp
			className={cn(cardVariants({ variant, className, clickable }))}
			{...props}
		/>
	);
};

export function BasicCardHeader({
	className,
	...props
}: ComponentPropsWithoutRef<'header'>) {
	return (
		<header
			className={cn('p-4', className)}
			{...props}
		/>
	);
}

export function BasicCardTitle({
	className,
	...props
}: ComponentPropsWithoutRef<'h5'>) {
	return (
		<h5
			className={cn('font-bold text-lg', className)}
			{...props}
		/>
	);
}

export function BasicCardSubTitle({
	className,
	...props
}: ComponentPropsWithoutRef<'h6'>) {
	return (
		<h6
			className={cn('text-base', className)}
			{...props}
		/>
	);
}

export function BasicCardText({
	className,
	...props
}: ComponentPropsWithoutRef<'p'>) {
	return (
		<p
			className={cn('text-sm text-on-surface-variant', className)}
			{...props}
		/>
	);
}

interface CardImageProps extends ComponentPropsWithoutRef<'img'> {
	asChild?: boolean;
	src: string;
	alt: string;
	width: number;
	height: number;
}

export function BasicCardImage({
	className,
	asChild = false,
	...props
}: CardImageProps) {
	const Comp = asChild ? Slot : 'img';
	return (
		<Comp
			className={cn('w-full object-cover rounded-xl', className)}
			{...props}
		/>
	);
}

export function BasicCardContent({
	className,
	...props
}: ComponentPropsWithoutRef<'div'>) {
	return (
		<div
			className={cn('p-4', className)}
			{...props}
		/>
	);
}

export function BasicCardMedia({
	className,
	...props
}: ComponentPropsWithoutRef<'div'>) {
	return (
		<div
			className={cn('relative', className)}
			{...props}
		/>
	);
}

export function BasicCardActions({
	className,
	...props
}: ComponentPropsWithoutRef<'div'>) {
	return (
		<div
			className={cn('flex justify-end gap-2', className)}
			{...props}
		/>
	);
}

export const BasicCardFooter = forwardRef<
	HTMLDivElement,
	HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn("flex items-center p-6 pt-0", className)}
		{...props}
	/>
))
BasicCardFooter.displayName = "CardFooter"


export { cardVariants };
